import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderInput } from '@kmdx/contracts';
import type { OrderStatus } from '@kmdx/contracts';
import { api } from '../client';
import { qk } from '../keys';
import type {
  AssignmentInput,
  CandidatesResult,
  OrderDetail,
  TripDetail,
  OrderFilters,
  OrderListItem,
  Paginated,
} from '../types';

function toQuery(filters: OrderFilters): Record<string, string | number | boolean | string[]> {
  const query: Record<string, string | number | boolean | string[]> = {};
  if (filters.status && filters.status.length > 0) query.status = filters.status;
  if (filters.partnerId) query.partnerId = filters.partnerId;
  if (filters.q) query.q = filters.q;
  if (filters.fromDate) query.fromDate = filters.fromDate;
  if (filters.toDate) query.toDate = filters.toDate;
  if (filters.country) query.country = filters.country;
  if (filters.unassignedOnly) query.unassignedOnly = true;
  if (filters.hardDeadlineOnly) query.hardDeadlineOnly = true;
  query.page = filters.page ?? 1;
  query.perPage = filters.perPage ?? 50;
  return query;
}

export function useOrders(filters: OrderFilters) {
  return useQuery({
    queryKey: qk.orders.list(filters),
    queryFn: () => api.get<Paginated<OrderListItem>>('/orders', toQuery(filters)),
    placeholderData: (prev) => prev,
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: qk.orders.detail(id ?? ''),
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderInput) => api.post<OrderDetail>('/orders', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
  });
}

export function useUpdateOrder(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<OrderInput>) => api.patch<OrderDetail>(`/orders/${id}`, input),
    onSuccess: (order) => {
      queryClient.setQueryData(qk.orders.detail(id), order);
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
    },
  });
}

export function useOrderTransition(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { status: OrderStatus; reason?: string; overrideDocumentGate?: boolean }) =>
      api.post<OrderDetail>(`/orders/${id}/status`, {
        to: payload.status,
        ...(payload.reason ? { reason: payload.reason } : {}),
        ...(payload.overrideDocumentGate ? { overrideDocumentGate: true } : {}),
      }),
    onSuccess: (order) => {
      queryClient.setQueryData(qk.orders.detail(id), order);
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
    },
  });
}

/**
 * Класацията от `/dispatch/candidates`. Не се кешира дълго — оставащите часове
 * на шофьора се менят с всяка минута кормуване.
 */
export function useCandidates(orderId: string | undefined) {
  return useQuery({
    queryKey: qk.dispatch.candidates(orderId ?? ''),
    queryFn: () => api.get<CandidatesResult>('/dispatch/candidates', { orderId: orderId ?? '' }),
    enabled: Boolean(orderId),
    staleTime: 15_000,
  });
}

export function useAssign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignmentInput) => api.post<TripDetail>('/dispatch/assign', input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: qk.orders.detail(input.orderId) });
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
      void queryClient.invalidateQueries({ queryKey: qk.board.all });
      void queryClient.invalidateQueries({ queryKey: qk.dispatch.all });
    },
  });
}
